import * as React from 'react'
import { Box, Form, ConfirmationModal, Grid, Typography } from 'cs-kevlar-react'
import { Formik } from 'formik'
import { EditModalActionBar } from '..'
import { Spacing } from 'cs-kevlar-react/lib/es/theming/baseConstants'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import {
  EditSupportUserForm,
  EditSupportUserFormSchema,
} from './EditSupportUserForm'
import {
  ApiPermissionsConfigurationViewModel,
  Guid,
  SupportUser,
  SupportUserPermission,
} from '../../api'
import {
  useSupportUsers,
  useSupportUserApi,
  usePageNotifications,
  useLogger,
} from 'hooks'
import {
  SupportUserSpreadPermissions,
  mapAccessLevelsToPermissions,
} from 'hooks/useSupportUserApi'

interface EditSupportUserProps {
  supportUser: SupportUser
  onClose: () => void
  onDeleted?: () => void
  permissionConfiguration: ApiPermissionsConfigurationViewModel | null
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    spacer: {
      flexGrow: 1,
      minHeight: 0,
    },
    formFields: {
      padding: theme.spacing(10),
      // setting the spacing on the Grid adds to the width and subtracts from the margin, but not enough
      marginLeft: -2 * Spacing,
      marginRight: -2 * Spacing,
    },
    form: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    formFieldContainer: {
      flexGrow: 1,
      minHeight: 0,
      overflowY: 'auto',
    },
  }),
)

export const EditSupportUser: React.FunctionComponent<EditSupportUserProps> = ({
  supportUser,
  onClose,
  permissionConfiguration,
}) => {
  const classes = useStyles({})
  const { logError } = useLogger()

  const {
    dispatch: dispatchSupportUserAction,
    spreadPermissions,
  } = useSupportUsers()

  const { editSupportUser } = useSupportUserApi()

  const { dispatch: dispatchNotification } = usePageNotifications()

  const isEditing = supportUser.id !== 'NEW'

  const isDisabled = supportUser.isDisabled

  const supportUserWithDefaults: SupportUserSpreadPermissions = spreadPermissions(
    supportUser,
  )

  const onSubmit = (
    values: SupportUserSpreadPermissions,
    formikActions: any,
  ) => {
    // Reconcile permissions
    values.permissions = mapAccessLevelsToPermissions(values)

    if (isEditing) onEdit(supportUser.id, values, onClose, formikActions)
  }

  const onEdit = (
    supportUserId: Guid,
    values: SupportUser,
    onComplete: () => void,
    formikActions: any,
  ) => {
    editSupportUser(supportUserId, values)
      .then((response) => {
        if (!response) return

        formikActions.setSubmitting(false)

        if (response.status === 200 && response.data.isSuccess) {
          const supportUserModel = spreadPermissions({
            ...response.data.data,
            id: response.data.data.id,
          })

          dispatchSupportUserAction({
            type: 'EditSupportUser',
            payload: supportUserModel,
          })

          onComplete()
        } else {
          dispatchNotification({
            type: 'SetError',
            payload: 'There was an error editing the support user',
          })
        }
      })
      .catch((error) => {
        formikActions.setSubmitting(false)

        logError(error)

        dispatchNotification({
          type: 'SetError',
          payload: 'There was an error editing the support user',
        })
      })
  }

  const onEnable = () => {
    const enabledSupportUser = {
      ...supportUser,
      isDisabled: false,
    }
    editSupportUser(supportUser.id, enabledSupportUser)
      .then((response) => {
        if (!response) return

        if (response.status === 200 && response.data.isSuccess) {
          const supportUserModel = spreadPermissions({
            ...response.data.data,
            id: response.data.data.id,
          })

          dispatchSupportUserAction({
            type: 'EditSupportUser',
            payload: supportUserModel,
          })

          onClose()
        } else {
          dispatchNotification({
            type: 'SetError',
            payload: 'There was an error editing the support user',
          })
        }
      })
      .catch((error) => {
        logError(error)

        dispatchNotification({
          type: 'SetError',
          payload: 'There was an error editing the support user',
        })
      })
  }

  const onDisable = () => {
    const enabledSupportUser = {
      ...supportUser,
      isDisabled: true,
    }
    editSupportUser(supportUser.id, enabledSupportUser)
      .then((response) => {
        if (!response) return

        if (response.status === 200 && response.data.isSuccess) {
          const supportUserModel = spreadPermissions({
            ...response.data.data,
            id: response.data.data.id,
          })

          dispatchSupportUserAction({
            type: 'EditSupportUser',
            payload: supportUserModel,
          })

          onClose()
        } else {
          dispatchNotification({
            type: 'SetError',
            payload: 'There was an error editing the support user',
          })
        }
      })
      .catch((error) => {
        logError(error)

        dispatchNotification({
          type: 'SetError',
          payload: 'There was an error editing the support user',
        })
      })
  }

  return (
    <React.Fragment>
      <Formik
        initialValues={supportUserWithDefaults}
        validationSchema={EditSupportUserFormSchema}
        onSubmit={onSubmit}
      >
        {(formikProps) => (
          <Form onSubmit={formikProps.handleSubmit} className={classes.form}>
            <Box className={classes.formFieldContainer}>
              <Grid
                container
                spacing={Spacing}
                direction="column"
                alignItems="flex-start"
                justifyContent="center"
                className={classes.formFields}
              >
                <EditSupportUserForm
                  formikProps={formikProps}
                  permissionConfiguration={permissionConfiguration}
                />
              </Grid>
            </Box>
            <Box className={classes.spacer} />
            <EditModalActionBar
              canDelete={isEditing}
              onDelete={() => (isDisabled ? onEnable() : onDisable())}
              onCancel={onClose}
              formikProps={formikProps}
              saveText={'Save'}
              deleteText={isDisabled ? 'Enable' : 'Disable'}
            />
          </Form>
        )}
      </Formik>
    </React.Fragment>
  );
}
